import { useRef, useEffect } from "react";
import { Link } from "wouter";
import { ArrowRight, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

interface HeroSectionProps {
  title?: string;
  subtitle?: string;
  description?: string;
  primaryButtonText?: string;
  primaryButtonHref?: string;
  secondaryButtonText?: string;
  secondaryButtonHref?: string;
  stat1Value?: string;
  stat1Label?: string;
  stat2Value?: string;
  stat2Label?: string;
  badgeText?: string;
}

export default function HeroSection({
  title = "Find your shape.",
  subtitle = "Find your style.",
  description = "Browse pieces from multiple brands, all filtered by your body shape and colour palette. Find what actually flatters you, discover Maria Personal Shopper picks, and shop with confidence.",
  primaryButtonText = "Shop Maria's Picks",
  primaryButtonHref = "/shop",
  secondaryButtonText = "Learn About Maria",
  secondaryButtonHref = "/about",
  stat1Value = "3000+",
  stat1Label = "Curated Items",
  stat2Value = "Trusted",
  stat2Label = "Brands you love",
  badgeText = "By Maria, For You"
}: HeroSectionProps) {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    // React doesn't reliably set the muted DOM property (React #10389),
    // which causes browsers to block autoplay. Set it imperatively.
    el.muted = true;
    el.play().catch(() => {});
  }, []);

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-warm-white to-cream dark:from-gray-900 dark:to-gray-800 py-16 lg:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-in">
            <Badge className="inline-flex items-center px-4 py-2 bg-soft-blush/10 text-soft-blush mb-6">
              <Star className="w-4 h-4 mr-2 fill-current" />
              {badgeText}
            </Badge>
            <h1 className="text-4xl md:text-5xl font-serif font-bold text-charcoal dark:text-white leading-tight mb-6">
              {title}{" "}
              <span className="text-soft-blush">{subtitle}</span>
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-300 mb-8 max-w-xl">
              {description}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                asChild
                size="lg"
              >
                <Link href={primaryButtonHref}>
                  {primaryButtonText}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="border-soft-blush text-soft-blush hover:bg-soft-blush hover:text-white"
              >
                <Link href={secondaryButtonHref}>{secondaryButtonText}</Link>
              </Button>
            </div>
          </div>
          <div className="relative animate-slide-up overflow-hidden">
            <video
              ref={videoRef}
              autoPlay
              muted
              loop
              playsInline
              // The same file client/index.html preloads at this width: the
              // poster is the home page's LCP image, and a preload for a
              // different file is a wasted download.
              poster={window.innerWidth < 640
                ? "https://cdn.capsulechichub.com/hero-video-poster-v3-800.webp"
                : "https://cdn.capsulechichub.com/hero-video-poster-v3.jpg"}
              src={window.innerWidth < 640
                ? "https://cdn.capsulechichub.com/hero-video-mobile-v3.mp4"
                : "https://cdn.capsulechichub.com/hero-video-v3.mp4"}
              width={1366}
              height={768}
              className="rounded-2xl shadow-2xl w-full h-auto"
            />
            <div className="absolute top-2 left-2 md:-top-4 md:-left-4 bg-white dark:bg-gray-800 p-3 md:p-4 rounded-lg shadow-lg">
              <div className="text-xl md:text-2xl font-serif font-bold text-soft-blush">{stat1Value}</div>
              <div className="text-xs md:text-sm text-gray-600 dark:text-gray-300">{stat1Label}</div>
            </div>
            <div className="absolute bottom-2 right-2 md:-bottom-4 md:-right-4 bg-white dark:bg-gray-800 p-3 md:p-4 rounded-lg shadow-lg">
              <div className="text-xl md:text-2xl font-serif font-bold text-soft-blush">{stat2Value}</div>
              <div className="text-xs md:text-sm text-gray-600 dark:text-gray-300">{stat2Label}</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}